import React, { useEffect, useState } from 'react';

import cx from 'classnames';
import PropTypes from 'prop-types';

import IconButton from 'components/IconButton';
import Modal from 'components/Modal/Modal';
import RoundedButton from 'components/RoundedButton/RoundedButton';

const REASON_MIN = 5;
const REASON_MAX = 500;

const validate = (value) => {
  const trimmed = (value || '').trim();
  if (trimmed.length < REASON_MIN) return `Reason must be at least ${REASON_MIN} characters`;
  if (trimmed.length > REASON_MAX) return `Reason cannot exceed ${REASON_MAX} characters`;
  return '';
};

const RevokeReasonModal = ({ isOpen, onClose, onSubmit, isSubmitting, tenant }) => {
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setReason('');
      setTouched(false);
    }
  }, [isOpen]);

  const error = touched ? validate(reason) : '';
  const isValid = !validate(reason);

  const handleSubmit = () => {
    setTouched(true);
    if (!isValid) return;
    onSubmit(reason.trim());
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose} showHeader={false} shouldCloseOnOverlayClick={false}>
      <div className='py-4 ps-5 pe-5'>
        <div className='border-btm d-flex justify-content-between pt-0 px-0 pb-3'>
          <h5 className='modal-title fw-bold-bold'>Revoke snapshot</h5>
          <IconButton icon='icon-cross-x' onClick={onClose} />
        </div>
        <div className='pt-4'>
          <p>
            Revoke the snapshot for <span className='fw-bold'>{tenant}</span>? The encrypted file will be deleted and
            the download link will stop working.
          </p>
          <div className='form-group input-hight-acm mb-3'>
            <label htmlFor='revoke-reason' className='label-title'>
              Reason <span className='text-danger'>*</span>
            </label>
            <textarea
              id='revoke-reason'
              rows={3}
              maxLength={REASON_MAX}
              placeholder={`Why are you revoking? (min ${REASON_MIN} chars)`}
              value={reason}
              onChange={(event) => {
                setReason(event.target.value);
                setTouched(true);
              }}
              onBlur={() => setTouched(true)}
              className={cx('form-control newClientInputs', { 'is-invalid': error })}
            />
            <div className='d-flex justify-content-between mt-1'>
              <div>{error && <span className='invalid-feedback d-block'>{error}</span>}</div>
              <small className='text-muted'>
                {reason.length}/{REASON_MAX}
              </small>
            </div>
          </div>
          <div className='d-flex align-items-center justify-content-end gap-2 p-3 border-t'>
            <RoundedButton text='Cancel' btnStyle='outlined' size='md' onClick={onClose} />
            <RoundedButton
              text={isSubmitting ? 'Revoking…' : 'Revoke snapshot'}
              btnStyle='contained'
              color='red'
              size='md'
              onClick={handleSubmit}
              isDisabled={!isValid || isSubmitting}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

RevokeReasonModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  tenant: PropTypes.string,
};

RevokeReasonModal.defaultProps = {
  isSubmitting: false,
  tenant: '',
};

export default RevokeReasonModal;
